import { z } from 'zod';
import { apiClient } from '../lib/apiClient';
import type { Aquarium, AquariumFormData } from '../types/aquarium';

export type { Aquarium } from '../types/aquarium';

export interface AquariumSummary {
  id: string;
  name: string;
  tankType: string | null;
  volumeLiters: number | null;
  createdAt: string;
}

const AquariumSchema = z.object({
  id: z.string(),
  ownerId: z.string().optional().nullable().transform((v) => v ?? ''),
  name: z.string(),
  description: z.string().nullable().optional().transform((v) => v ?? null),
  tankType: z.string().nullable().optional().transform((v) => v ?? null),
  grossVolume: z.number().nullable().optional().transform((v) => v ?? null),
  netVolume: z.number().nullable().optional().transform((v) => v ?? null),
  setupDate: z.string().nullable().optional().transform((v) => v ?? null),
  imageUrl: z.string().nullable().optional().transform((v) => v ?? null),
  createdAt: z.string(),
  updatedAt: z.string().optional().transform((v) => v ?? ''),
  supplySystem: z.string().nullable().optional().transform((v) => v ?? null),
  equipment: z.array(z.unknown()).optional().transform((v) => v ?? []),
  length: z.number().nullable().optional().transform((v) => v ?? null),
  width: z.number().nullable().optional().transform((v) => v ?? null),
  height: z.number().nullable().optional().transform((v) => v ?? null),
  sumpAvailable: z.boolean().optional().transform((v) => v ?? false),
  equipmentNotes: z.string().nullable().optional().transform((v) => v ?? null),
  notes: z.string().nullable().optional().transform((v) => v ?? null),
  isDeleted: z.boolean().optional().transform((v) => v ?? false),
});

export interface CreateAquariumInput {
  name: string;
  description?: string | null;
  tankType?: string | null;
  grossVolume?: number | null;
  netVolume?: number | null;
  setupDate?: string | null;
  sumpAvailable?: boolean | null;
  equipmentNotes?: string | null;
  supplySystem?: string | null;
  equipment?: unknown[];
  length?: number | null;
  width?: number | null;
  height?: number | null;
  notes?: string | null;
}

export async function fetchAquariums(): Promise<Aquarium[]> {
  const response = await apiClient.get<{ data: unknown[] | { data: unknown[] } }>('/api/v1/aquariums');
  const responseData = response.data;
  const items = Array.isArray(responseData)
    ? responseData
    : (responseData as { data: unknown[] }).data;
  return items.map((item) => AquariumSchema.parse(item) as unknown as Aquarium);
}

export async function createAquarium(input: CreateAquariumInput): Promise<Aquarium> {
  // Only send what the user actually entered: unset optional fields are
  // omitted (the backend applies its defaults) — never `null` for the
  // non-nullable `sump_available` boolean.
  const fields: Record<string, unknown> = {
    name: input.name,
    description: input.description,
    tank_type: input.tankType,
    gross_volume: input.grossVolume,
    net_volume: input.netVolume,
    setup_date: input.setupDate,
    sump_available: input.sumpAvailable ?? false,
    equipment_notes: input.equipmentNotes,
    supply_system: input.supplySystem,
    equipment: input.equipment ?? [],
    length: input.length,
    width: input.width,
    height: input.height,
    notes: input.notes,
  };
  const payload = Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined && value !== null),
  );

  const response = await apiClient.post<{ data: { data: unknown } } | { data: unknown }>(
    '/api/v1/aquariums',
    payload,
  );
  const envelope = response as { data: { data?: unknown } };
  const raw = envelope.data?.data ?? envelope.data;
  return AquariumSchema.parse(raw) as unknown as Aquarium;
}

export async function updateAquarium(id: string, input: AquariumFormData): Promise<Aquarium> {
  const payload = {
    name: input.name,
    description: input.description ?? null,
    tank_type: input.tankType ?? null,
    gross_volume: input.grossVolume ?? null,
    net_volume: input.netVolume ?? null,
    setup_date: input.setupDate ?? null,
    // Omitted when unanswered — `null` is not a valid value for this boolean.
    ...(input.sumpAvailable != null ? { sump_available: input.sumpAvailable } : {}),
    equipment_notes: input.equipmentNotes ?? null,
    supply_system: input.supplySystem ?? null,
    equipment: input.equipment,
    length: input.length ?? null,
    width: input.width ?? null,
    height: input.height ?? null,
    notes: input.notes ?? null,
  };

  const response = await apiClient.put<{ data: { data: unknown } } | { data: unknown }>(
    `/api/v1/aquariums/${id}`,
    payload,
  );
  const envelope = response as { data: { data?: unknown } };
  const raw = envelope.data?.data ?? envelope.data;
  return AquariumSchema.parse(raw) as unknown as Aquarium;
}

export async function deleteAquarium(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${id}`);
}
